<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'

const props = defineProps<{
  src?: string | null
  alt?: string
  class?: string
}>()

const currentSrc = ref<string | null>(null)
const showInitials = ref(false)
let fallbackAttempt = 0
let fallbackUrls: string[] = []

/**
 * Extract the company domain from a logo URL.
 * Handles unavatar.io URLs (https://unavatar.io/example.com)
 * and regular website URLs (https://www.example.com/path).
 */
const extractDomain = (url: string): string | null => {
  try {
    // Handle Google Favicon API URLs: extract the target domain from query params
    const googleFaviconMatch = url.match(/[?&](?:domain|url)=(?:https?:\/\/)?([^&]+)/)
    if (googleFaviconMatch?.[1]) return googleFaviconMatch[1].replace(/^www\./, '')
    // Handle unavatar.io URLs: https://unavatar.io/{domain}
    const unavatarMatch = url.match(/unavatar\.io\/([^/?#]+)/)
    if (unavatarMatch?.[1]) return unavatarMatch[1]
    // For regular URLs, extract the hostname
    const parsed = new URL(url)
    const host = parsed.hostname.replace(/^www\./, '')
    // Skip image hosting/favicon domains — we want the company domain, not the CDN
    const imageHosts = ['imgur.com', 'cloudinary.com', 'amazonaws.com', 'googleusercontent.com', 'gstatic.com', 'google.com']
    if (imageHosts.some(h => host.endsWith(h))) return null
    return host
  } catch {
    return null
  }
}

const buildFallbacks = (url: string): string[] => {
  const sources: string[] = []
  const domain = extractDomain(url)
  if (domain) {
    // Google Favicon V2 API — use direct URL for better results
    sources.push(`https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${domain}&size=128`)
  }
  // Skip unavatar URLs (service returns generic placeholders for all domains)
  if (!url.includes('unavatar.io')) {
    sources.push(url)
  }
  return sources
}

const initials = (() => {
  const name = props.alt || ''
  return name.slice(0, 2).toUpperCase() || '?'
})()

const loadImage = (url: string | null | undefined) => {
  fallbackAttempt = 0
  showInitials.value = false
  if (!url) {
    showInitials.value = true
    return
  }
  fallbackUrls = buildFallbacks(url)
  currentSrc.value = fallbackUrls[0] ?? null
}

const onError = () => {
  fallbackAttempt++
  if (fallbackAttempt < fallbackUrls.length) {
    currentSrc.value = fallbackUrls[fallbackAttempt] ?? null
  } else {
    currentSrc.value = null
    showInitials.value = true
  }
}

watch(() => props.src, (newVal) => {
  loadImage(newVal)
})

onMounted(() => {
  loadImage(props.src)
})
</script>

<template>
  <img
    v-if="!showInitials && currentSrc"
    :src="currentSrc"
    :alt="alt || 'Supplier Logo'"
    :class="props.class"
    @error="onError"
  />
  <div
    v-else
    :class="props.class"
    class="bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 font-bold select-none"
    :title="alt"
  >
    {{ initials }}
  </div>
</template>
